/**
 * Manager dashboard for attendants with the manage_team permission.
 * Shows subordinate shifts; lets the manager edit any of them via the shared modal.
 */

import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { apiGet } from '../../src/api/client';
import Card from '../../src/components/Card';
import Button from '../../src/components/Button';
import ContentContainer from '../../src/components/ContentContainer';
import EditShiftModal, { ShiftLike } from '../../src/components/EditShiftModal';
import { useTheme } from '../../src/context/ThemeContext';
import type { ColorTheme } from '../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { titleCase } from '../../src/utils/format';

interface ManagerShift {
  id: string;
  attendantId: string;
  attendantName: string | null;
  locationId: string;
  locationName: string | null;
  clockInTime: string;
  clockOutTime: string | null;
  status: string;
  notes: string | null;
  lockedAt: string | null;
}

interface AttendantProfile {
  locations: Array<{
    locationId: string;
    locationName: string;
    resolvedPermissions: { manage_team?: boolean };
  }>;
}

export default function TeamScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [shifts, setShifts] = useState<ManagerShift[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ShiftLike | null>(null);

  const fetchAll = async () => {
    try {
      const profile = await apiGet<AttendantProfile>('/api/attendants/me');
      const canManage = profile.locations.some(l => l.resolvedPermissions?.manage_team);
      setHasAccess(canManage);
      if (!canManage) {
        setLoading(false);
        return;
      }
      const data = await apiGet<{ shifts: ManagerShift[] }>('/api/manager/shifts');
      setShifts(data.shifts);
    } catch (err: unknown) {
      console.error('team fetch failed', err);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(useCallback(() => { fetchAll(); }, []));

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={colors.primary} size="large" />
      </View>
    );
  }

  if (!hasAccess) {
    return (
      <View style={styles.center}>
        <Ionicons name="lock-closed-outline" size={48} color={colors.textLight} />
        <Text style={styles.muted}>You don't have the Manage Team permission.</Text>
      </View>
    );
  }

  const active = shifts.filter(s => s.status === 'active');
  const completed = shifts.filter(s => s.status !== 'active').slice(0, 30);

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ padding: spacing.md }}>
      <ContentContainer>
        <Text style={styles.h1}>Team Timecards</Text>

        <Card style={{ marginBottom: spacing.md }}>
          <Text style={styles.sectionTitle}>Currently Clocked In ({active.length})</Text>
          {active.length === 0 ? (
            <Text style={styles.muted}>No one on your team is clocked in right now.</Text>
          ) : (
            active.map(s => {
              const start = new Date(s.clockInTime);
              const hours = ((Date.now() - start.getTime()) / 3_600_000).toFixed(1);
              return (
                <View key={s.id} style={styles.shiftRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.primary}>{titleCase(s.attendantName) || '(unnamed)'} · {s.locationName || '?'}</Text>
                    <Text style={styles.secondary}>since {start.toLocaleString()} ({hours}h elapsed)</Text>
                  </View>
                  <Button
                    title="Edit"
                    size="sm"
                    variant="outline"
                    onPress={() => setEditing({
                      id: s.id, attendantName: s.attendantName, locationName: s.locationName,
                      clockInTime: s.clockInTime, clockOutTime: s.clockOutTime,
                      notes: s.notes, status: s.status, lockedAt: s.lockedAt,
                    })}
                    icon={<Ionicons name="pencil" size={14} color={colors.primary} />}
                  />
                </View>
              );
            })
          )}
        </Card>

        <Card style={{ marginBottom: spacing.md }}>
          <Text style={styles.sectionTitle}>Recent Completed Shifts</Text>
          {completed.length === 0 ? (
            <Text style={styles.muted}>No completed shifts yet.</Text>
          ) : (
            completed.map(s => {
              const start = new Date(s.clockInTime);
              const end = s.clockOutTime ? new Date(s.clockOutTime) : null;
              const hours = end ? ((end.getTime() - start.getTime()) / 3_600_000).toFixed(2) : '—';
              return (
                <View key={s.id} style={styles.shiftRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.primary}>{titleCase(s.attendantName) || '(unnamed)'} · {s.locationName || '?'}</Text>
                    <Text style={styles.secondary}>
                      {start.toLocaleString()} → {end ? end.toLocaleString() : '(active)'} · {hours}h
                      {s.lockedAt && <Text style={{ color: colors.warning }}>  🔒</Text>}
                    </Text>
                  </View>
                  <Button
                    title="Edit"
                    size="sm"
                    variant="outline"
                    onPress={() => setEditing({
                      id: s.id, attendantName: s.attendantName, locationName: s.locationName,
                      clockInTime: s.clockInTime, clockOutTime: s.clockOutTime,
                      notes: s.notes, status: s.status, lockedAt: s.lockedAt,
                    })}
                    icon={<Ionicons name="pencil" size={14} color={colors.primary} />}
                  />
                </View>
              );
            })
          )}
        </Card>

        <EditShiftModal
          visible={editing !== null}
          shift={editing}
          onClose={() => setEditing(null)}
          onSaved={fetchAll}
        />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.lg, gap: spacing.sm },
  h1: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.sm },
  muted: { fontSize: fontSize.sm, color: c.textLight, textAlign: 'center', paddingVertical: spacing.md },
  shiftRow: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight,
  },
  primary: { fontSize: fontSize.sm, color: c.text },
  secondary: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
